"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useContext, useEffect, type FormEvent } from "react";
import { CartContext } from "../shopping/CartContext";
import { ShoppingCart as LucideShoppingCart, Check, Loader2, Bell, Star } from 'lucide-react'; // Ajout de Star
import { FiHeart } from 'react-icons/fi';
import { useWishlist } from '../wishlist/WishlistContext';
import { API_BASE_URL, API_UPLOAD_URL, getImageUrl } from '@/app/lib/api';

// --- INTERFACES ---
export interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  category: { id: string; name: string; colorHome?: string; colorCataloge?: string };
  images: string[];
  description?: string;
  about_product?: string[];
  promoPrice?: number | null;
  stock?: number;
  sku?: string;
  createdAt?: string;
  videoUrl?: string | null;
  ficheTechnique?: string | null;
  [key: string]: any;
}

interface Review {
  id?: string;
  productId?: string;
  note?: number;
  nom?: string;
  name?: string;
  commentaire?: string;
  comment?: string;
  texte?: string;
  description?: string;
  image?: string | null;
  createdAt?: string | Date;
  [key: string]: any;
}

interface ProductCardProps {
  product: Product;
  className?: string;
  priority?: boolean;
}

const formatPrice = (p?: number | string | null) => {
  const n = Number(p ?? 0);
  if (Number.isNaN(n)) return "0.000 DT";
  return `${n.toFixed(3)} DT`;
};

// Composant Helper pour les étoiles
const StarRating = ({ rating, size = 12 }: { rating: number, size?: number }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          size={size}
          className={`${i < Math.round(rating)
            ? "fill-yellow-400 text-yellow-400"
            : "fill-transparent stroke-current text-gray-300"
            }`}
        />
      ))}
    </div>
  );
};

const WishlistToggle = ({ product, childCategoryName }: { product: Product, childCategoryName?: string }) => {
  const ctx = useWishlist();
  const wished = ctx.isWished(String(product.id));

  const onToggle = () => ctx.toggle({
    id: String(product.id),
    name: String(product.name ?? ''),
    price: Number(product.price ?? 0) || undefined,
    image: Array.isArray(product.images) ? product.images[0] : product.images ?? undefined,
    slug: product.slug ?? String(product.id),
    parentCategoryName: (product?.category?.name) || (typeof childCategoryName !== 'undefined' ? childCategoryName : undefined),
  });

  return (
    <button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(); }}
      className={`p-2 sm:p-2.5 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 active:scale-90 ${wished
        ? 'bg-red-500 text-white'
        : 'bg-white/90 text-gray-600 hover:bg-white hover:text-red-500'
        }`}
    >
      <FiHeart size={16} className={wished ? "fill-current" : ""} />
    </button>
  );
};

export default function ProductCard({ product, className = "", priority = false }: ProductCardProps) {
  const cartContext = useContext(CartContext);
  if (!cartContext) throw new Error("ProductCard must be used within a CartProvider");

  const { cartItems, addToCart, openCart } = cartContext;
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isNotifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyAlert, setNotifyAlert] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [isSubmittingNotify, setIsSubmittingNotify] = useState(false);

  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState<boolean>(true);
  const [reviewsOpen, setReviewsOpen] = useState<boolean>(false);

  const priceNum = Number(product.price ?? 0);
  const promoNum = Number(product.promoPrice ?? NaN);
  const isPromo = !Number.isNaN(promoNum) && promoNum > 0 && promoNum < priceNum;
  const isOutOfStock = product.stock === 0;
  const finalPrice = isPromo ? promoNum : priceNum;

  const NEW_DAYS = 15;
  const isNew = (() => {
    try {
      if (!product.createdAt) return false;
      const d = new Date(product.createdAt);
      if (Number.isNaN(d.getTime())) return false;
      return (Date.now() - d.getTime()) <= NEW_DAYS * 24 * 60 * 60 * 1000;
    } catch { return false; }
  })();

  const childCategoryName = product.childCategoryName ?? product.category?.name;
  const isInCart = !isOutOfStock && cartItems.some((item) => item.id === product.id);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isOutOfStock || isAddingToCart) return;
    setIsAddingToCart(true);
    addToCart({
      id: product.id,
      name: product.name,
      price: finalPrice,
      image: product.images?.[0] || "/logo.png.webp",
      quantity: 1,
      slug: product.slug,
      parentCategoryName: childCategoryName,
    });
    setTimeout(() => {
      setIsAddingToCart(false);
      openCart();
    }, 600);
  };

  useEffect(() => {
    let mounted = true;
    const fetchReviews = async () => {
      setReviewsLoading(true);
      try {
        const base = API_BASE_URL || "";
        const res = await fetch(`${base}/reviews/product/${encodeURIComponent(product.id)}`, { cache: 'no-store' });
        if (!mounted) return;
        if (!res.ok) { setReviews([]); return; }
        const data = await res.json();
        const list: Review[] = Array.isArray(data) ? data : (data.reviews || data.items || data.data || []);
        setReviews(list || []);
      } catch (err) {
        setReviews([]);
      } finally {
        if (mounted) setReviewsLoading(false);
      }
    };
    fetchReviews();
    return () => { mounted = false; };
  }, [product.id]);

  const average = reviews.length > 0 ? (reviews.reduce((s, r) => s + (Number(r.note) || 0), 0) / reviews.length) : 0;

  const submitNotify = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmittingNotify(true);
    try {
      const base = API_BASE_URL || "";
      const res = await fetch(`${base}/stock-subscriptions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId: product.id, email: notifyEmail }),
      }).catch(() => { throw new Error("Failed to fetch") });
      if (!res.ok) throw new Error();
      setNotifyAlert({ type: "success", message: "Nous vous informerons du retour en stock." });
      setTimeout(() => setNotifyModalOpen(false), 2500);
    } catch {
      setNotifyAlert({ type: "error", message: "Une erreur est survenue." });
    } finally {
      setIsSubmittingNotify(false);
    }
  };

  return (
    <>
      <div className={`group relative h-full rounded-xl p-[1px] bg-transparent transition-all duration-700 hover:bg-gradient-to-r hover:from-[#a91b8d] hover:to-blue-500 hover:shadow-xl sm:hover:-translate-y-1.5 ${className}`}>
        <div className="relative flex flex-col h-full bg-white rounded-[11px]">

        {/* IMAGE SECTION */}
        <div className="relative w-full aspect-[4/5] overflow-hidden rounded-t-xl bg-white">
          <Link href={`/produit/${encodeURIComponent(product.slug || product.id)}`} className="block w-full h-full">
            <div className="relative w-full h-full overflow-hidden bg-white">
              <Image
                src={product.images?.[0] ? getImageUrl(product.images[0]) : "/logo.png.webp"}
                alt={product.name}
                fill
                unoptimized
                className={`object-cover transition-transform duration-700 ease-out sm:group-hover:scale-110 p-4 ${isOutOfStock ? 'grayscale-[0.3]' : ''}`}
                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                priority={priority}
                loading={priority ? "eager" : "lazy"}
              />
              {product.images?.[1] && (
                <Image
                  src={getImageUrl(product.images[1])}
                  alt={product.name}
                  fill
                  unoptimized
                  className="absolute inset-0 object-cover opacity-0 transition-opacity duration-700 sm:group-hover:opacity-100 sm:group-hover:scale-110"
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                />
              )}
            </div>
          </Link>

          <div className="absolute top-2 right-2 z-10 sm:top-3 sm:right-3 transition-all duration-300 lg:translate-x-4 lg:opacity-0 lg:group-hover:translate-x-0 lg:group-hover:opacity-100">
            <WishlistToggle product={product} childCategoryName={childCategoryName} />
          </div>

          <div className="absolute top-2 left-2 flex flex-col gap-1.5 z-10 sm:top-3 sm:left-3">
            {isPromo && !isOutOfStock && (
              <span className="bg-red-500 text-white text-[9px] sm:text-[10px] font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full shadow-sm animate-pulse">
                -{Math.round(((priceNum - promoNum) / Math.max(priceNum, 1)) * 100)}%
              </span>
            )}
            {isNew && (
              <span className="bg-emerald-500 text-white text-[9px] sm:text-[10px] font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full shadow-sm">
                Nouveau
              </span>
            )}
            {isOutOfStock && (
              <span className="bg-main/90 backdrop-blur-sm text-white text-[9px] sm:text-[10px] font-semibold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full">
                Rupture
              </span>
            )}
          </div>
        </div>

        {/* CONTENT SECTION */}
        <div className="flex flex-col flex-grow p-3 sm:p-4">
          <Link href={`/produit/${encodeURIComponent(product.slug || product.id)}`} className="flex-grow">
            <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1 block truncate">
              {childCategoryName || "Général"}
            </span>
            <h3 className="text-xs sm:text-sm font-semibold text-gray-800 line-clamp-2 min-h-[32px] sm:min-h-[40px] mb-2 group-hover:text-black transition-colors leading-tight">
              {product.name}
            </h3>

            <div className="flex items-center justify-between mb-3">
              <div className="flex items-baseline gap-1.5">
                <span className="text-sm sm:text-lg font-black text-gray-900">
                  {formatPrice(finalPrice)}
                </span>
                {isPromo && (
                  <span className="text-[10px] sm:text-xs text-gray-400 line-through">
                    {formatPrice(product.price)}
                  </span>
                )}
              </div>

              {/* REVIEWS MODIFIÉES */}
              <div className="text-right">
                {reviewsLoading ? (
                  <Loader2 className="animate-spin text-gray-300" size={14} />
                ) : (
                  <div className="flex flex-col items-end gap-0.5">
                    <StarRating rating={average} />
                    {reviews.length > 0 && (
                      <button
                        onClick={(e) => { e.preventDefault(); setReviewsOpen(v => !v); }}
                        className="text-[10px] text-gray-400 hover:text-main hover:underline transition-colors"
                      >
                        ({reviews.length} avis)
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Link>

          <div className="mt-auto">
            {isOutOfStock ? (
              <button
                onClick={(e) => { e.preventDefault(); setNotifyModalOpen(true); }}
                className="w-full py-2 sm:py-2.5 bg-gray-50 border border-gray-200 text-gray-600 rounded-lg text-[10px] sm:text-xs font-bold flex items-center justify-center gap-2 transition-all hover:bg-gray-100"
              >
                <Bell size={14} /> <span className="hidden xs:inline">M'ALERTER</span><span className="xs:hidden">ALERTE</span>
              </button>
            ) : (
              <button
                onClick={handleAddToCart}
                disabled={isAddingToCart || isInCart}
                className={`w-full py-2.5 sm:py-3 rounded-lg text-[10px] sm:text-xs font-bold tracking-wider uppercase transition-all duration-300 flex items-center justify-center gap-2 transform active:scale-95 ${isInCart
                  ? "bg-green-500 text-white"
                  : "bg-main text-white hover:bg-main hover:shadow-lg"
                  }`}
              >
                {isAddingToCart ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : isInCart ? (
                  <Check size={16} className="animate-bounce" />
                ) : (
                  <LucideShoppingCart size={16} />
                )}
                <span className="truncate">
                  {isAddingToCart ? "En cours..." : isInCart ? "Dans le panier" : "Ajouter au panier"}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Liste des avis détaillée */}
      {reviewsOpen && reviews.length > 0 && (
        <div className="mt-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm text-sm">
          <div className="space-y-3 max-h-64 overflow-y-auto custom-scrollbar pr-2">
            {reviews.map((r) => {
              const comment = r.commentaire || r.comment || r.texte || r.description || '';
              const author = r.nom || r.name || 'Anonyme';
              const date = r.createdAt ? new Date(r.createdAt).toLocaleDateString() : undefined;
              return (
                <div key={String(r.id ?? Math.random())} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-semibold text-gray-800 text-xs">{author}</div>
                    <div className="flex items-center gap-2">
                      <StarRating rating={r.note ?? 0} size={10} />
                      {date && <span className="text-[9px] text-gray-400">{date}</span>}
                    </div>
                  </div>
                  {comment ? <div className="text-gray-600 text-[11px] leading-relaxed">{comment}</div> : <div className="text-gray-400 italic text-[10px]">Aucun commentaire</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* NOTIFY MODAL */}
      {isNotifyModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setNotifyModalOpen(false)} />
          <div className="relative bg-white w-full max-w-sm rounded-t-2xl sm:rounded-2xl shadow-2xl p-6 animate-in slide-in-from-bottom sm:zoom-in-95">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 text-gray-900 mb-4"><Bell size={24} /></div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Produit épuisé</h3>
              <p className="text-xs text-gray-500 mb-6">Laissez votre email pour être averti dès son retour en stock.</p>
              <form onSubmit={submitNotify} className="space-y-4">
                <input
                  type="email"
                  required
                  placeholder="votre@email.com"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-900 outline-none text-sm"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                />
                {notifyAlert && (
                  <div className={`p-3 rounded-lg text-xs ${notifyAlert.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {notifyAlert.message}
                  </div>
                )}
                <div className="flex gap-3">
                  <button type="button" onClick={() => setNotifyModalOpen(false)} className="flex-1 py-3 text-sm font-semibold text-gray-500">Annuler</button>
                  <button type="submit" disabled={isSubmittingNotify} className="flex-[2] bg-gray-900 text-white py-3 rounded-xl text-sm font-bold disabled:opacity-50">
                    {isSubmittingNotify ? "Envoi..." : "M'avertir"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}